import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

type ProductionDeclarationMaterialCommentFormProps = {
  comment: string;
  description?: string;
  isPending?: boolean;
  onCancel: () => void;
  onChange: (comment: string) => void;
  onSubmit: () => void;
  submitLabel?: string;
};

export function ProductionDeclarationMaterialCommentForm({
  comment,
  description,
  isPending = false,
  onCancel,
  onChange,
  onSubmit,
  submitLabel,
}: ProductionDeclarationMaterialCommentFormProps) {
  const commentId = useId();
  const { t } = useTranslation('productionDeclaration', {
    useSuspense: false,
  });
  const { t: tCommon } = useTranslation('common', {
    useSuspense: false,
  });
  const isCommentBlank = comment.trim() === '';

  return (
    <form
      className='space-y-4'
      onSubmit={(event) => {
        event.preventDefault();
        if (!(isCommentBlank || isPending)) {
          onSubmit();
        }
      }}
    >
      <label
        className='block font-medium text-declaration-foreground text-sm'
        htmlFor={commentId}
      >
        {description ?? t('materialCommodityOrigins.comments.description')}
      </label>
      <Textarea
        className='field-sizing-fixed min-h-28 resize-none border-declaration-border bg-white text-declaration-foreground shadow-none placeholder:text-declaration-muted-foreground focus-visible:border-declaration-primary focus-visible:ring-declaration-primary/20 dark:border-declaration-border dark:bg-white dark:text-declaration-foreground'
        disabled={isPending}
        id={commentId}
        onChange={(event) => onChange(event.target.value)}
        placeholder={t('materialCommodityOrigins.comments.placeholder')}
        value={comment}
      />
      <div className='flex justify-end gap-4'>
        <Button
          className='h-11 rounded-lg border-declaration-border bg-white px-8 font-medium text-declaration-foreground shadow-none hover:bg-declaration-hover dark:border-declaration-border dark:bg-white dark:text-declaration-foreground dark:hover:bg-declaration-hover'
          onClick={onCancel}
          type='button'
          variant='outline'
        >
          {tCommon('cancel')}
        </Button>
        <Button
          className='h-11 rounded-lg px-8 font-medium'
          disabled={isCommentBlank || isPending}
          type='submit'
          variant='declaration-primary'
        >
          {submitLabel ?? tCommon('add')}
        </Button>
      </div>
    </form>
  );
}
